import styled from '@emotion/styled';
import {TextField} from '@mui/material';
import React, {RefObject, useEffect, useRef} from 'react';

import {
  mixinFocus,
  mixinFocusError,
  mixinInputDisabled,
  mixinInputError,
  mixinInputValid,
} from '@ELib/styles/mixins';
import {classy} from '@ELib/utilities/HelperFunctions';

import {FormInputWrapper} from './FormInputWrapper';
import {stopKeyboardEventPropagation} from './keyboardEventPropgation';
import {SharedProps} from './sharedProps';

export type ETextInputProps = SharedProps & {
  endAdornment?: React.ReactElement;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeydown?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  value?: string;
  type?: React.HTMLInputTypeAttribute;
  autoComplete?: string;
  selectTextOnFocus?: boolean;
  min?: number;
  max?: number;
  step?: number;
  inputRef?: RefObject<HTMLInputElement>;
  largeEndAdornment?: boolean;
  align?: 'left' | 'right';
  hideErrorMessage?: boolean;
  size?: 'small' | 'normal' | 'large';
  hideArrows?: boolean;
};

export const ETextInput = ({
  label,
  labelInfo,
  endAdornment,
  onChange,
  onFocus,
  onBlur,
  onKeydown,
  bottomText,
  placeholder,
  name,
  value,
  type,
  disabled,
  autoComplete = 'off',
  autoFocus,
  selectTextOnFocus,
  ariaLabel,
  error,
  errorMessage,
  min,
  max,
  step,
  inputRef,
  largeEndAdornment = false,
  align = 'left',
  hideErrorMessage = false,
  size = 'normal',
  hideArrows = false,
}: ETextInputProps): React.ReactElement => {
  const elementRef = useRef<HTMLInputElement>(null);

  const inputProps: {
    'data-testid': string;
    min?: number;
    max?: number;
    step?: number;
    'aria-label'?: string;
    ref: RefObject<HTMLInputElement>;
    onKeyDown: (
      e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void;
  } = {
    'data-testid': `ETextInput-${name}`,
    'aria-label': ariaLabel,
    ref: inputRef ?? elementRef,
    ...(min !== undefined && {min}),
    ...(max !== undefined && {max}),
    ...(step !== undefined && {step}),
    onKeyDown: stopKeyboardEventPropagation,
  };

  useEffect(() => {
    if (autoFocus && inputProps.ref.current?.value) {
      inputProps.ref.current.select();
    }
  }, [autoFocus, inputProps.ref]);

  const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (selectTextOnFocus) {
      e.target.select();
    }
    onFocus?.(e);
  };

  return (
    <FormInputWrapper
      bottomText={bottomText}
      className='ETextInput'
      error={error}
      errorMessage={!hideErrorMessage ? errorMessage : undefined}
      label={label}
      labelInfo={labelInfo}
      labelProps={{htmlFor: name}}
    >
      {/* note: this div is needed so that the end adornment has the correct height placement when bottomText or error exists */}
      <div style={{position: 'relative'}}>
        <StyledInput
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className={classy({
            'end-adornment': Boolean(endAdornment),
            'large-end-adornment': largeEndAdornment,
            'right-align': align === 'right',
            'size-small': size === 'small',
            'size-large': size === 'large',
            'hide-arrows': hideArrows,
          })}
          disabled={disabled}
          error={error}
          id={name}
          inputProps={inputProps}
          name={name}
          placeholder={placeholder}
          type={type}
          value={value ?? ''}
          onBlur={onBlur}
          onChange={onChange}
          onFocus={handleOnFocus}
          onKeyDown={onKeydown}
        />
        {endAdornment ? (
          <AdornmentWrapper>{endAdornment}</AdornmentWrapper>
        ) : null}
      </div>
    </FormInputWrapper>
  );
};

const StyledInput = styled(TextField)<{error?: boolean}>`
  box-sizing: border-box;
  width: 100%;

  .MuiInputBase-root {
    input {
      ${({error}) => (error ? mixinInputError : mixinInputValid)};
      padding: var(--input-padding);

      &.Mui-disabled {
        ${mixinInputDisabled}
      }
    }

    .MuiOutlinedInput-notchedOutline {
      border: none;
    }
  }

  &.end-adornment .MuiInputBase-root input {
    padding-right: var(--input-adornment-padding);
  }

  &.large-end-adornment .MuiInputBase-root input {
    padding-right: var(--input-adornment-large-padding);
  }

  &.right-align .MuiInputBase-root input {
    text-align: right;
  }

  &.size-small .MuiInputBase-root input {
    font-size: var(--font-size-small);
    height: var(--input-condensed-height);
    padding: var(--input-condensed-padding);
  }

  &.size-large .MuiInputBase-root input {
    font-size: var(--font-size-subtitle);
    font-weight: var(--font-weight-subtitle);
  }

  // Hide arrows/spinners on number input

  &.hide-arrows input[type='number'] {
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
`;

const AdornmentWrapper = styled.div`
  svg {
    color: var(--color-core-link);
  }
  position: absolute;
  top: 50%;
  right: var(--gutter-tiny);
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StyledDateTimeTextField = styled(TextField)<{error?: boolean}>`
  box-sizing: border-box;
  width: 100%;

  .MuiInputBase-root {
    min-height: var(--input-min-height);
    ${({error}) => (error ? mixinInputError : mixinInputValid)};

    /** 
     * The focused styling needs to be applied manually here since the mixinInputError/mixinInputvalid
     * is not applied to the input directly, but instead a wrapper div which does not have the pseudoclass :focus
     */
    &.Mui-focused {
      ${({error}) => (error ? mixinFocusError : mixinFocus)};
    }

    &.Mui-disabled {
      ${mixinInputDisabled}
    }

    input {
      padding: 0;
    }

    .MuiOutlinedInput-notchedOutline {
      border: none;
    }
  }
`;
